/*
 * Copyright '2025' Dell Inc. or its subsidiaries. All Rights Reserved.
 */
import { IEventBus } from "sirius-platform-support-library/shared/event-bus/event-bus.interface";
import { IDialogsService } from "sirius-platform-support-library/shared/dialogs/dialogs-service.interface";
import { RequestedDialog } from "sirius-platform-support-library/shared/dialogs/dialog";
import { DialogSize } from "sirius-platform-support-library/shared/dialogs/dialog-size";
import {
  ActionTypeEnum,
  ButtonStyle,
  ButtonType,
} from "sirius-platform-support-library/models/common";
import { TranslationService } from "sirius-platform-support-library/shared/localization/translations/translation.service";
import { SessionStorageService } from "../../services/session-storage/session-storage.service";
import { FeedbackService } from "../services/feedback.service";

export const FeedbackHandlerTypeName = "FeedbackHandler";

export class FeedbackHandler {
  public static readonly FEEDBACK_ACTION_EVENT =
    "dell.tdm.navigation.feedback.action";

  private eventBus: IEventBus;
  private dialogsService: IDialogsService;
  private translateService: TranslationService;
  private sessionStorageService: SessionStorageService;
  private feedbackService: FeedbackService;

  private dialog: RequestedDialog = {} as RequestedDialog;
  private checkboxValue: boolean = false;
  private checkboxDescription: string;
  private feedbackDescription: string;

  public constructor(
    eventBus: IEventBus,
    dialogsService: IDialogsService,
    translateService: TranslationService,
    sessionStorageService: SessionStorageService,
    feedbackService?: FeedbackService
  ) {
    this.eventBus = eventBus;
    this.dialogsService = dialogsService;
    this.translateService = translateService;
    this.sessionStorageService = sessionStorageService;
    this.feedbackService = feedbackService;
    this.createDialog();
  }

  public registerFeedbackHandler(): void {
    this.register(
      this,
      FeedbackHandler.FEEDBACK_ACTION_EVENT,
      this.handleFeedbackAction
    );
  }

  private register<T>(
    context: any,
    eventName: string,
    callback: (event: T | undefined) => void
  ): void {
    this.eventBus.registerBroadcast<T>(
      this,
      FeedbackHandlerTypeName,
      eventName,
      (event) => {
        callback.call(context, event?.data);
      }
    );
  }

  private createDialog(): void {
    this.dialog.size = DialogSize.MEDIUM;
    this.dialog.modal = true;
    this.dialog.closeButtonVisible = true;
    this.dialog.showBackdrop = true;
    this.dialog.closeOnClickOutside = true;
    this.dialog.title = { localizationCode: "DIALOGS.FEEDBACK.TITLE" };
    this.dialog.bodyControl = {
      name: "tdp-feedback",
      attributes: {
        checkboxValue: this.checkboxValue.toString(),
      },
    };
    this.dialog.actions = [
      {
        type: ActionTypeEnum.CALLBACK,
        code: "DIALOGS.FEEDBACK.CANCEL",
        action: this.dialogActionCallback,
        closeAfterAction: true,
        localizationCode: "DIALOGS.FEEDBACK.CANCEL",
        buttonStyle: ButtonStyle.PRIMARY,
        buttonType: ButtonType.SOLID,
      },
    ];
    this.dialog.onTranslationsRequested =
      this.onTranslationsRequested.bind(this);
  }

  private async handleFeedbackAction(): Promise<void> {
    await this.getFeedback();
    const dialog = {
      ...this.dialog,
      bodyControl: {
        name: this.dialog.bodyControl.name,
        attributes: {
          checkboxValue: this.checkboxValue.toString(),
          checkboxDescription: this.checkboxDescription,
          feedbackDescription: this.feedbackDescription,
        },
      },
    };
    await this.dialogsService.open(this, dialog);
  }

  private async getFeedback(): Promise<void> {
    try {
      const response = await this.feedbackService.getFeedback();
      this.checkboxValue = response?.current_state ?? false;
      this.checkboxDescription = await this.translateService.translate(
        "DIALOGS.FEEDBACK.CHECKBOX_DESCRIPTION"
      );
      this.feedbackDescription = await this.translateService.translate(
        "DIALOGS.FEEDBACK.DESCRIPTION"
      );
    } catch (e) {
      console.error(
        "An exception has been encountered while trying to fetch feedback",
        e
      );
      throw e;
    }
  }

  private dialogActionCallback(): void {
    try {
      const checkboxValue =
        this.sessionStorageService.getItem(
          FeedbackHandler.FEEDBACK_ACTION_EVENT
        ) ?? this.checkboxValue;
      if (checkboxValue) {
        this.feedbackService.setFeedback(checkboxValue);
      }
      this.sessionStorageService.removeItem(
        FeedbackHandler.FEEDBACK_ACTION_EVENT
      );
    } catch (e) {
      console.error(
        "An exception has been encountered while trying to set feedback",
        e
      );
    }
  }

  private async onTranslationsRequested(): Promise<Record<string, any>> {
    return this.translateService.getTranslationsBySupportedLocales();
  }
}
